import { useEffect, useMemo, useRef, useState } from 'react';
import { useBranding } from '../context/BrandingContext';

/**
 * Casting Calls UK — splash / launch screen.
 *
 * Lives in two modes:
 *   • "live"    — gated by session + standalone rules (shown on cold start).
 *   • "preview" — on-demand for admins previewing branding changes. Never
 *                 touches storage, never counts against the live session
 *                 guard, never blocks the UI for regular users.
 *
 * -------- Live trigger rules --------
 *  1. Runtime guard: a module-level flag set SYNCHRONOUSLY on first
 *     mount-that-decides-to-show. Prevents any re-mount of SplashScreen
 *     (StrictMode double-invoke, re-parented tree, etc.) from replaying.
 *  2. sessionStorage flag `ccuk_splash_v2_session_shown` — the cross-reload
 *     guard within a single browser tab / PWA app session.
 *  3. Installed PWA (`display-mode: standalone`) — skips cooldown entirely
 *     but still honours the session & runtime guards, so you get one
 *     splash per fresh app launch.
 *  4. Browser tab — 60-minute localStorage cooldown keyed on a versioned
 *     key so future design bumps can force a one-time re-display.
 *
 * -------- Preview mode --------
 *  Accepts `overrides` (branding-shaped dict) and an `onDone` callback.
 *  Ignores trigger rules, storage, runtime guard. Useful for admin UIs.
 */

const SCHEMA_VERSION = 'v2';
const SESSION_FLAG = `ccuk_splash_${SCHEMA_VERSION}_session_shown`;
const LAST_SEEN_KEY = `ccuk_splash_${SCHEMA_VERSION}_last_seen_at`;
const COOLDOWN_MS = 60 * 60 * 1000;

const ENTER_MS = 500;
const HOLD_MS = 100;
const LOGO_MS = 300;
const EXIT_MS = 200;
const TOTAL_MS = ENTER_MS + HOLD_MS + LOGO_MS + EXIT_MS;
const REDUCED_TOTAL_MS = 280;

const FALLBACK_LOGO = '/ccuk-logo.png';

// Module-level runtime guard. Set exactly once per JS runtime when a live
// splash is permitted to show — survives React re-mounts (StrictMode
// double-mount, parent re-renders, provider updates, route changes) so
// the splash can never flicker or replay within a single session.
let SPLASH_ALREADY_SHOWN_THIS_RUNTIME = false;

// ---------- storage helpers (all wrapped for iOS private mode) ----------
function safeSessionGet(key) {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key, value) {
  try { window.sessionStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeLocalGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeLocalSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
}

function isStandalone() {
  try {
    if (typeof window === 'undefined') return false;
    const mm = window.matchMedia && window.matchMedia('(display-mode: standalone)');
    if (mm && mm.matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch { /* ignore */ }
  return false;
}

function prefersReducedMotion() {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch { return false; }
}
function handleSplashImageError(e) {
  try {
    const img = e.currentTarget;

    // Prevent an infinite fallback loop if the fallback logo also fails.
    if (img.dataset.fallbackApplied === '1') return;

    img.dataset.fallbackApplied = '1';
    img.src = FALLBACK_LOGO;
  } catch {
    // fail silently
  }
}
/**
 * Decide whether the splash should show right now AND, if yes, atomically
 * mark it as shown so any concurrent remount returns false.
 *
 * This must be synchronous — React may mount-unmount-mount the component
 * in a tick before useEffect runs. Setting the guard here closes that
 * race-condition window.
 */
function claimLiveSplashSlot() {
  if (typeof window === 'undefined') return false;
  if (SPLASH_ALREADY_SHOWN_THIS_RUNTIME) return false;
  try {
    if (safeSessionGet(SESSION_FLAG)) {
      // Persist the runtime guard even when sessionStorage already says
      // "shown" — cheap, avoids re-checking storage on every remount.
      SPLASH_ALREADY_SHOWN_THIS_RUNTIME = true;
      return false;
    }
    const standalone = isStandalone();
    if (!standalone) {
      const last = parseInt(safeLocalGet(LAST_SEEN_KEY) || '0', 10);
      if (last && Date.now() - last < COOLDOWN_MS) {
        SPLASH_ALREADY_SHOWN_THIS_RUNTIME = true;
        return false;
      }
    }
    // ATOMIC: claim the slot now, before React has a chance to re-run us.
    SPLASH_ALREADY_SHOWN_THIS_RUNTIME = true;
    safeSessionSet(SESSION_FLAG, '1');
    safeLocalSet(LAST_SEEN_KEY, String(Date.now()));
    return true;
  } catch {
    SPLASH_ALREADY_SHOWN_THIS_RUNTIME = true;
    return false;
  }
}

// ---------- pure visual — used by both live and preview modes ----------
function SplashVisual({ stage, config, reduced }) {
  const {
    bg, textColor, accent, splashText, logoUrl,
  } = config;

  const chars = useMemo(() => Array.from(splashText), [splashText]);
  const exiting = stage === 'exit';

  return (
    <div
      role="status"
      aria-label={`${splashText} — loading`}
      data-testid="ccuk-splash-screen"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: exiting ? 0 : 1,
        pointerEvents: exiting ? 'none' : 'auto',
        transition: `opacity ${reduced ? REDUCED_TOTAL_MS : EXIT_MS}ms ease-out`,
      }}
    >
      {reduced ? (
        <img
          src={logoUrl}
          alt=""
          style={{ maxWidth: '55%', maxHeight: '28%', objectFit: 'contain' }}
          onError={handleSplashImageError}
        />
      ) : (
        <div
          style={{
            position: 'relative',
            width: 'min(84vw, 520px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(14px, 3vh, 28px)',
          }}
        >
          <img
            src={logoUrl}
            alt={splashText}
            onError={(e) => { e.target.src = FALLBACK_LOGO; }}
            style={{
              height: 'clamp(90px, 18vh, 160px)',
              width: 'auto',
              maxWidth: '70%',
              objectFit: 'contain',
              opacity: stage === 'logo' ? 1 : 0,
              transform: stage === 'logo' ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
              transition: `opacity ${LOGO_MS}ms ease-out, transform ${LOGO_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
              filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.25))',
            }}
          />

          <div
            aria-hidden="true"
            className="ccuk-splash-wordmark"
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              whiteSpace: 'pre',
              color: textColor,
              fontFamily: '"Bebas Neue", "Inter", system-ui, sans-serif',
              fontSize: 'clamp(28px, 6.4vw, 56px)',
              letterSpacing: '0.04em',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {chars.map((ch, i) => (
              <span
                key={`${ch}-${i}`}
                className="ccuk-splash-char"
                style={{ animationDelay: `${Math.min(i * 55, 700)}ms` }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </div>

          <div
            aria-hidden="true"
            style={{
              width: 'clamp(80px, 18vw, 160px)',
              height: 2,
              background: accent,
              transformOrigin: 'left center',
              opacity: 0.85,
              animation: `ccuk-splash-underline ${ENTER_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1) both`,
              animationDelay: `${Math.max(0, ENTER_MS - 400)}ms`,
              animationIterationCount: 1,
              borderRadius: 2,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes ccuk-splash-char-in {
          from { opacity: 0; transform: translate3d(0, 6px, 0); filter: blur(3px); }
          to   { opacity: 1; transform: translate3d(0, 0, 0); filter: blur(0); }
        }
        @keyframes ccuk-splash-underline {
          from { transform: scaleX(0); opacity: 0; }
          60%  { opacity: 0.85; }
          to   { transform: scaleX(1); opacity: 0.85; }
        }
        .ccuk-splash-char {
          display: inline-block;
          opacity: 0;
          will-change: opacity, transform, filter;
          animation-name: ccuk-splash-char-in;
          animation-duration: 420ms;
          animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
          animation-fill-mode: both;
          animation-iteration-count: 1;
        }
      `}</style>
    </div>
  );
}

// ---------- shared stage driver ----------
function useSplashStages(active, reduced, onDone) {
  const [stage, setStage] = useState(active ? 'enter' : 'done');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return undefined;
    if (reduced) {
      const t = setTimeout(() => {
        setStage('done');
        if (onDoneRef.current) onDoneRef.current();
      }, REDUCED_TOTAL_MS);
      return () => clearTimeout(t);
    }
    const t1 = setTimeout(() => setStage('logo'), ENTER_MS + HOLD_MS);
    const t2 = setTimeout(() => setStage('exit'), ENTER_MS + HOLD_MS + LOGO_MS);
    const t3 = setTimeout(() => {
      setStage('done');
      if (onDoneRef.current) onDoneRef.current();
    }, TOTAL_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // Only re-run if `active` flips from false to true (preview re-plays).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduced]);

  return stage;
}

function buildConfig(source) {
  return {
    bg: source?.splash_bg_color || '#002FA7',
    textColor: source?.splash_text_color || '#FFFFFF',
    accent: source?.splash_accent_color || '#6A8CFF',
    splashText: (source?.splash_text || 'Casting Calls UK').trim() || 'Casting Calls UK',
    logoUrl: source?.splash_logo_url
      || source?.logo_url_dark
      || source?.logo_url
      || FALLBACK_LOGO,
  };
}

/**
 * Default export — the LIVE splash that App.js mounts once. Never replays
 * thanks to the module-level runtime guard + synchronous sessionStorage
 * claim. Also honours `branding.splash_enabled === false`.
 */
export default function SplashScreen() {
  const { branding } = useBranding();
  const enabled = branding?.splash_enabled !== false;

  // Claim the live splash slot once.
  // The module-level runtime guard prevents replays during route/auth/branding changes.
  const [allowed] = useState(() => enabled && claimLiveSplashSlot());

  // Freeze motion preference for this splash run.
  const [reduced] = useState(prefersReducedMotion);

  // Freeze splash branding at the moment the splash starts.
  // This prevents BrandingContext updates from swapping logo/colours mid-animation,
  // which can look like flashing or replaying on the login page.
  const [config] = useState(() => buildConfig(branding));

  const stage = useSplashStages(allowed, reduced, null);

  if (!allowed || stage === 'done') return null;

  return <SplashVisual stage={stage} config={config} reduced={reduced} />;
}

/**
 * Preview splash — mount with a key change to replay. Completely bypasses
 * all trigger/storage logic. Calls `onDone()` when the full animation
 * (or reduced-motion static) completes. Admin-only caller is responsible
 * for unmounting.
 */
export function SplashPreview({ overrides, onDone }) {
  const [reduced] = useState(prefersReducedMotion);
  const stage = useSplashStages(true, reduced, onDone);
  if (stage === 'done') return null;
  const config = buildConfig(overrides || {});
  return <SplashVisual stage={stage} config={config} reduced={reduced} />;
}
